import { createPluginRegistration } from '@embedpdf/core';
import { EmbedPDF } from '@embedpdf/core/react';
import { useSharedPdfiumEngine } from './sharedEngine';
import {
  DocumentContent,
  DocumentManagerPluginPackage,
} from '@embedpdf/plugin-document-manager/react';
import { InteractionManagerPluginPackage } from '@embedpdf/plugin-interaction-manager/react';
import { PanPluginPackage } from '@embedpdf/plugin-pan/react';
import {
  RenderLayer,
  RenderPluginPackage,
} from '@embedpdf/plugin-render/react';
import { Scroller, ScrollPluginPackage } from '@embedpdf/plugin-scroll/react';
import {
  Viewport,
  ViewportPluginPackage,
} from '@embedpdf/plugin-viewport/react';
import {
  ZoomGestureWrapper,
  ZoomPluginPackage,
} from '@embedpdf/plugin-zoom/react';
import { useMemo } from 'react';

interface Props {
  url: string;
  className?: string;
}

/**
 * Minimal headless EmbedPDF viewer for comparison with the drop-in
 * <PDFViewer>. Renders viewport + scroll + zoom + pan + render layers.
 * No toolbar, no sidebar, no chrome at all — just the document.
 */
export function PdfViewerHeadless({ url, className }: Props) {
  const { engine, isLoading } = useSharedPdfiumEngine();

  // Stable plugin registrations — re-creating these every render
  // re-registers and re-mounts the document.
  const plugins = useMemo(
    () => [
      createPluginRegistration(DocumentManagerPluginPackage, {
        initialDocuments: [{ url }],
      }),
      // viewportGap = outer padding around the document inside the
      // viewport. defaultPageGap = vertical space between pages. Both
      // configurable so the viewer fills the lightbox edge-to-edge but
      // pages still have breathing room.
      createPluginRegistration(ViewportPluginPackage, { viewportGap: 16 }),
      createPluginRegistration(ScrollPluginPackage, { defaultPageGap: 12 }),
      createPluginRegistration(RenderPluginPackage),
      createPluginRegistration(ZoomPluginPackage),
      // Pan requires interaction-manager (capability dependency).
      createPluginRegistration(InteractionManagerPluginPackage),
      createPluginRegistration(PanPluginPackage),
    ],
    [url],
  );

  if (isLoading || !engine) {
    return (
      <div className="flex h-full w-full items-center justify-center text-white">
        Loading PDF engine…
      </div>
    );
  }

  return (
    <div className={className} style={{ height: '100%', width: '100%' }}>
      <EmbedPDF engine={engine} plugins={plugins}>
        {({ activeDocumentId }) =>
          activeDocumentId && (
            <DocumentContent documentId={activeDocumentId}>
              {({ isLoaded }) =>
                isLoaded && (
                  <Viewport
                    documentId={activeDocumentId}
                    style={{ background: 'transparent' }}
                  >
                    <ZoomGestureWrapper documentId={activeDocumentId}>
                      <Scroller
                        documentId={activeDocumentId}
                        renderPage={({ width, height, pageIndex }) => (
                          <div
                            style={{
                              width,
                              height,
                              background: 'white',
                              boxShadow: '0 2px 8px rgba(0,0,0,0.4)',
                            }}
                          >
                            <RenderLayer
                              documentId={activeDocumentId}
                              pageIndex={pageIndex}
                            />
                          </div>
                        )}
                      />
                    </ZoomGestureWrapper>
                  </Viewport>
                )
              }
            </DocumentContent>
          )
        }
      </EmbedPDF>
    </div>
  );
}
